import { useTranslation } from "@i18n";
import { useRef, useState, useCallback, useMemo } from "react";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { GrClose } from "react-icons/gr";
import { assignTasksToSchedule, getScheduleDetails } from "../../../actions/task";
import { unassignedTasksUrl } from "../../../services/dataManager";
import SlickGrid1, {
  typedColumns,
  type ColumnDefinition,
  type GridRowBase,
  type SlickGrid1Ref,
} from "../../../cubeComponent/html/SlickGrid1";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";
import ButtonComponent from "../../../cubeComponent/form/ButtonComponent";

import { renderUserTag } from "@CubeComponents/html/userTagDom";

/**
 * Row of the unassigned-tasks grid (remote source `unassignedTasksUrl`). `"createdBy.fullName"` is
 * the dotted path SlickGrid reads at runtime, declared alongside the real nested `createdBy` object
 * so the column `field` type-checks.
 */
interface UnassignedTaskRow extends GridRowBase {
  _id: ObjectIdString;
  title?: string;
  createdBy?: { _id?: ObjectIdString; fullName?: string };
  "createdBy.fullName"?: string;
}

interface ExistingTasksProps {
  scheduleId: ObjectIdString;
  projectId: ObjectIdString;
  spaceId?: ObjectIdString;
  /** Reloads the caller's schedule grid and closes this modal. */
  refreshGrid: () => void;
  setTaskModal: (value: boolean) => void;
}

function TasksList(props: ExistingTasksProps) {
  const { t } = useTranslation();
  const dataGrid = useRef<SlickGrid1Ref<UnassignedTaskRow> | null>(null);
  const dispatch = useDispatch();
  const scheduleId = props.scheduleId;
  const projectId = props.projectId;
  const spaceId = props.spaceId;

  const [selectedTasks, setSelectedTasks] = useState<ObjectIdString[]>([]);

  const query = useMemo(
    () => ({
      params: {
        scheduleId,
        projectId,
        spaceId,
      },
    }),
    [scheduleId, projectId, spaceId],
  );

  const rowSelected = () => {
    if (dataGrid.current) {
      const selectedRecords = dataGrid.current.dataView.getAllSelectedItems();
      setSelectedTasks(selectedRecords.map((task) => task._id));
    }
  };

  const addTaskToSchedule = async () => {
    const selectedData = dataGrid.current?.dataView.getAllSelectedItems() || [];
    const taskIds = selectedData.map((task) => task._id);
    await dispatch(
      assignTasksToSchedule({
        ganttId: scheduleId,
        selectedTasks: taskIds,
        projectId,
      }),
    );
    setSelectedTasks([]);
    props.refreshGrid();
    dispatch(getScheduleDetails({ scheduleId }));
  };

  // Annotated against the key it is used with, so it satisfies `typedColumns`' per-field formatter
  // type instead of the widened `keyof` one.
  const addedByFormatter: NonNullable<ColumnDefinition<UnassignedTaskRow, "createdBy.fullName">["formatter"]> = (
    row,
    cell,
    value,
    columnDef,
    dataContext,
  ) => {
    const { _id } = (dataContext.createdBy || {}) as { _id?: ObjectIdString };
    if (!_id) return "";
    const container = document.createElement("div");
    renderUserTag(container, { id: _id });
    return container;
  };

  const columnDefinitions = useMemo(
    () =>
      typedColumns<UnassignedTaskRow>([
        {
          id: "title",
          field: "title",
          name: t("Title"),
          minWidth: 150,
          clipMode: "EllipsisWithTooltip",
        },
        {
          id: "createdBy",
          field: "createdBy.fullName",
          name: t("Assignee"),
          minWidth: 150,
          formatter: addedByFormatter,
          // _valueGetter returns the user ID so the formatter can render user info
          _valueGetter: (row: UnassignedTaskRow) => row.createdBy?._id,
          // For excel export, use the display name
          _excelValueGetter: (row: UnassignedTaskRow) => row.createdBy?.fullName || row.createdBy?._id || "",
          disableFiltering: true,
          disableSorting: true,
          disableGrouping: true,
        },
      ]),
    [],
  );

  const getGridComponent = useCallback(
    () => (
      <SlickGrid1
        ref={dataGrid}
        remoteDataSource={unassignedTasksUrl}
        query={query}
        columnDefinitions={columnDefinitions}
        onSelectedRowIdsChanged={rowSelected}
        rowHeight={68}
        gridHeight="100%"
        enableFilteringOption
        enableSortingOption
        enableColumnChooser
        infiniteScroll
        hideNoDataIllustration
      />
    ),
    [query, columnDefinitions],
  );

  return (
    <div className="fixed inset-0 z-[1000] bg-[var(--background-scrim)]">
      <div className="fixed start-1/2 top-1/2 h-[70%] w-[37.5rem] -translate-x-1/2 -translate-y-1/2 rounded bg-white p-[2rem]">
        <div className="mb-[1rem] flex w-full flex-row justify-between">
          <div className="text-[1.375rem] font-semibold">{t("Existing Tasks")}</div>
          <GrClose size={20} className="cursor-pointer" onClick={() => props.setTaskModal(false)} />
        </div>
        <div className="h-[80%]">{getGridComponent()}</div>
        <div className="mt-[2rem] flex flex-row justify-between">
          <ButtonComponent buttonType="gradientButton" disabled={selectedTasks.length == 0} onClick={addTaskToSchedule}>
            {t("Add")}
          </ButtonComponent>
        </div>
      </div>
    </div>
  );
}

export default TasksList;
